import React, { useState } from "react";
import WizardPageWrapper, { GeneralPageSummary } from "../WizardPageWrapper";
import Flex from "../../../../../../components/Flex";
import { Form, Input, Typography, Checkbox, Radio } from "antd";
import FormGroupItem from "../FormGroupItem";
import { CoverWizardForm, FormGroupTouched } from "../../CoverWizardModal";

type AutocoverPageProps = {
  onNextButtonClick?: () => void;
  onBackButtonClick?: () => void;
  //the current form values are needed in order to have field values at mount (useWatch outputs current values at second rerender)
  initialFormValues?: CoverWizardForm;
  formGroupTouchedState: [FormGroupTouched, React.Dispatch<FormGroupTouched>];
  formIsSubmitting: boolean;
};

const yesOrNoRadios = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const hasAutocoverFieldName = "hasAutocover";

const autocoverNeedsToBeCoveredFieldName = "autocoverNeedsToBeCovered";
const autocoverBoxWidthFieldName = "autocoverBoxWidth";
const autocoverDeckTypeFieldName = "autocoverDeckType";
const autocoverWidthBehindBoxFieldName = "autocoverWidthBehindBox";

export default function AutocoverPage({
  onNextButtonClick,
  onBackButtonClick,
  initialFormValues,
  formIsSubmitting,
  formGroupTouchedState: [formGroupTouched, setFormGroupTouched],
}: AutocoverPageProps) {
  const form = Form.useFormInstance<CoverWizardForm>();

  const hasAutocoverAnswer =
    Form.useWatch(hasAutocoverFieldName, { form, preserve: true }) ??
    initialFormValues?.[hasAutocoverFieldName];
  const hasAutocover = hasAutocoverAnswer === "yes";

  const isAutocoverCoveredAnswer =
    Form.useWatch(autocoverNeedsToBeCoveredFieldName, {
      form,
      preserve: true,
    }) ?? initialFormValues?.[autocoverNeedsToBeCoveredFieldName];
  const isAutocoverCovered = isAutocoverCoveredAnswer === "yes";

  return (
    <WizardPageWrapper
      title="Autocover"
      submitOnNext
      summary={<GeneralPageSummary />}
      onBackButtonClick={onBackButtonClick}
      nextIsLoading={formIsSubmitting}
    >
      <Flex vertical gap="16px">
        <FormGroupItem
          disabled={formIsSubmitting}
          label="Autocover"
          closeButtonText={formGroupTouched.autocoverGroup ? "Save" : "Next"}
          onGroupClosed={() =>
            setFormGroupTouched({
              ...formGroupTouched,
              autocoverGroup: true,
            })
          }
          notExpandedDisplayValue={hasAutocoverAnswer === "yes" ? "Yes" : "No"}
          expandedOnMount={!formGroupTouched.autocoverGroup}
        >
          <Form.Item<CoverWizardForm>
            name={hasAutocoverFieldName}
            style={{ margin: "0" }}
            label="Does the pool have an autocover?"
          >
            <Radio.Group disabled={formIsSubmitting} options={yesOrNoRadios} />
          </Form.Item>
        </FormGroupItem>
        {hasAutocover && formGroupTouched.autocoverGroup && (
          <FormGroupItem
            disabled={formIsSubmitting}
            label="Autocover covering"
            closeButtonText="Save"
            onGroupClosed={() =>
              setFormGroupTouched({
                ...formGroupTouched,
                autocoverCoverGroup: true,
              })
            }
            notExpandedDisplayValue={isAutocoverCovered ? `Yes` : "No"}
            expandedOnMount={!formGroupTouched.autocoverCoverGroup}
          >
            <Flex vertical gap="1rem">
              <Flex vertical gap="0.5rem">
                <Typography.Text>
                  Does the autocover need to be covered?
                </Typography.Text>
                <Radio.Group
                  disabled={formIsSubmitting}
                  options={yesOrNoRadios}
                  value={isAutocoverCoveredAnswer}
                  onChange={(event) => {
                    const newValue = event.target.value;
                    if (newValue === "no") {
                      return form.setFieldsValue({
                        [autocoverNeedsToBeCoveredFieldName]: newValue,
                        [autocoverBoxWidthFieldName]: "",
                        [autocoverDeckTypeFieldName]: "",
                        [autocoverWidthBehindBoxFieldName]: "",
                      });
                    }
                    form.setFieldValue(
                      autocoverNeedsToBeCoveredFieldName,
                      newValue
                    );
                  }}
                />
              </Flex>
              {isAutocoverCovered && (
                <>
                  <Form.Item<CoverWizardForm>
                    style={{ margin: "0" }}
                    name={autocoverBoxWidthFieldName}
                    label="Width of autocover box"
                  >
                    <Input
                      disabled={formIsSubmitting}
                      style={{ maxWidth: "282px", width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item<CoverWizardForm>
                    style={{ margin: "0" }}
                    name={autocoverDeckTypeFieldName}
                    label="Deck type"
                  >
                    <Input
                      disabled={formIsSubmitting}
                      style={{ maxWidth: "282px", width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item<CoverWizardForm>
                    style={{ margin: "0" }}
                    name={autocoverWidthBehindBoxFieldName}
                    label="Width behind autocover box"
                  >
                    <Input
                      disabled={formIsSubmitting}
                      style={{ maxWidth: "282px", width: "100%" }}
                    />
                  </Form.Item>
                  <Typography.Text italic>
                    If yes, include photos, plus measurements and location of
                    the autocover box on the pool sketch.
                  </Typography.Text>
                </>
              )}
            </Flex>
          </FormGroupItem>
        )}
      </Flex>
    </WizardPageWrapper>
  );
}
